/**
 * Shared marketing illustrations.
 *
 * These five inline SVGs are the brand-coloured product illustrations used
 * across our marketing surfaces (the SignIn carousel and the public Landing
 * page). Centralised here so both pages stay pixel-identical and the brand
 * palette changes propagate to one place.
 *
 * Why inline SVG (and not an <img src>):
 *   - The illustrations participate in our brand tokens (--brand-navy,
 *     --brand-blue, --brand-sky, --brand-green) so they re-skin if we ever
 *     adjust the palette.
 *   - They render without a network round-trip on first paint.
 *   - The CanadaHostedIllustration is the one exception — it loads the
 *     canonical Canadian flag maple leaf as a PNG (/canadian-flag-leaf.png)
 *     because reproducing the 11-point flag silhouette in SVG never quite
 *     looks right.
 */

export function CoverageGapIllustration() {
  return (
    <svg
      viewBox="0 0 220 110"
      role="img"
      aria-label="Dragging a shift onto a three-day calendar; the middle day is flagged amber as an uncovered gap"
      className="w-full h-24"
    >
      <g transform="translate(72 4)">
        <rect x="0" y="0" width="44" height="14" rx="2" fill="hsl(var(--brand-navy))" />
        <text x="22" y="10" fontSize="7" fill="white" textAnchor="middle" fontWeight="600">9-5</text>
        <g transform="translate(38 11) rotate(20)">
          <path d="M 0 0 L 0 12 L 3 10 L 6 15 L 8 14 L 5 9 L 9 9 Z" fill="white" stroke="hsl(var(--brand-navy))" strokeWidth="0.8" />
        </g>
      </g>
      <path
        d="M 94 22 L 94 30 M 91 27 L 94 30 L 97 27"
        fill="none"
        stroke="#F59E0B"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <g transform="translate(20 35)">
        <rect x="0" y="0" width="55" height="42" rx="4" fill="white" stroke="hsl(var(--brand-navy))" strokeWidth="1" />
        <text x="27" y="11" fontSize="8" fill="hsl(var(--brand-navy))" textAnchor="middle" fontWeight="600">MON</text>
        <rect x="6" y="20" width="43" height="14" rx="2" fill="hsl(var(--brand-blue))" />
        <rect x="61" y="0" width="55" height="42" rx="4" fill="#FEF3C7" stroke="#F59E0B" strokeWidth="2" />
        <text x="88" y="11" fontSize="8" fill="#92400E" textAnchor="middle" fontWeight="600">TUE</text>
        <text x="88" y="32" fontSize="16" fill="#F59E0B" textAnchor="middle" fontWeight="700">!</text>
        <rect x="122" y="0" width="55" height="42" rx="4" fill="white" stroke="hsl(var(--brand-navy))" strokeWidth="1" />
        <text x="149" y="11" fontSize="8" fill="hsl(var(--brand-navy))" textAnchor="middle" fontWeight="600">WED</text>
        <rect x="128" y="20" width="43" height="14" rx="2" fill="hsl(var(--brand-blue))" />
      </g>
      <text x="110" y="98" fontSize="9" fill="hsl(var(--brand-navy))" textAnchor="middle" fontWeight="500">
        Drop a shift to close the gap
      </text>
    </svg>
  );
}

export function TimeOffConflictIllustration() {
  return (
    <svg
      viewBox="0 0 220 110"
      role="img"
      aria-label="Mock of the time-off request panel showing two shifts that conflict with the requested vacation dates"
      className="w-full h-24"
    >
      <defs>
        <clipPath id="req-card-shape-shared">
          <rect x="18" y="6" width="184" height="98" rx="6" />
        </clipPath>
      </defs>
      <rect x="18" y="6" width="184" height="98" rx="6" fill="white" stroke="hsl(var(--brand-navy))" strokeWidth="1.2" />
      <g clipPath="url(#req-card-shape-shared)">
        <rect x="18" y="6" width="184" height="18" fill="hsl(var(--brand-navy))" />
      </g>
      <text x="26" y="18" fontSize="8.5" fill="white" fontWeight="600">
        Sarah Patel · Apr 5–7 vacation
      </text>
      <circle cx="28" cy="38" r="4.5" fill="#F59E0B" />
      <text x="28" y="41" fontSize="7" fill="white" fontWeight="700" textAnchor="middle">!</text>
      <text x="38" y="41" fontSize="8" fill="#92400E" fontWeight="600">2 shifts conflict</text>
      <text x="30" y="56" fontSize="7" fill="hsl(var(--brand-navy))">• Apr 5 · 9–5 (Sarah covers)</text>
      <text x="30" y="68" fontSize="7" fill="hsl(var(--brand-navy))">• Apr 6 · 9–5 (Sarah covers)</text>
      <rect x="113" y="82" width="40" height="14" rx="3" fill="hsl(var(--brand-navy))" />
      <text x="133" y="92" fontSize="7" fill="white" textAnchor="middle" fontWeight="600">Approve</text>
      <rect x="158" y="82" width="36" height="14" rx="3" fill="white" stroke="hsl(var(--brand-navy))" strokeWidth="0.8" />
      <text x="176" y="92" fontSize="7" fill="hsl(var(--brand-navy))" textAnchor="middle" fontWeight="600">Deny</text>
    </svg>
  );
}

export function PrintFriendlyIllustration() {
  return (
    <svg
      viewBox="0 0 220 110"
      role="img"
      aria-label="A printed monthly pharmacy schedule pinned to a wall with a red thumbtack"
      className="w-full h-24"
    >
      <g transform="rotate(-3 110 56)">
        <rect x="46" y="15" width="130" height="86" rx="2" fill="hsl(var(--brand-navy))" fillOpacity="0.10" />
      </g>
      <g transform="rotate(-3 110 56)">
        <rect x="44" y="13" width="130" height="86" rx="2" fill="white" stroke="hsl(var(--brand-navy))" strokeWidth="0.8" />
        <rect x="44" y="13" width="130" height="10" fill="hsl(var(--brand-navy))" />
        <text x="109" y="20" fontSize="6.5" fill="white" textAnchor="middle" fontWeight="600">
          April 2026 · Soso Pharmacy
        </text>
        {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((d, i) => (
          <text key={`d${i}`} x={51 + i * 18.4} y={31} fontSize="5" fill="hsl(var(--brand-navy))" fontWeight="600">{d}</text>
        ))}
        <g stroke="hsl(var(--brand-sky))" strokeWidth="0.3">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <line key={`v${i}`} x1={44 + 18.5 * i} y1="33" x2={44 + 18.5 * i} y2="99" />
          ))}
          {[1, 2, 3, 4].map((i) => (
            <line key={`h${i}`} x1="44" y1={33 + 13.2 * i} x2="174" y2={33 + 13.2 * i} />
          ))}
        </g>
        <g fill="hsl(var(--brand-blue))">
          <rect x="48" y="44" width="14" height="3" rx="1" />
          <rect x="66" y="44" width="14" height="3" rx="1" />
          <rect x="85" y="44" width="14" height="3" rx="1" />
          <rect x="103" y="44" width="14" height="3" rx="1" />
          <rect x="122" y="44" width="14" height="3" rx="1" />
          <rect x="48" y="57" width="14" height="3" rx="1" />
          <rect x="66" y="57" width="14" height="3" rx="1" />
          <rect x="103" y="57" width="14" height="3" rx="1" />
          <rect x="122" y="57" width="14" height="3" rx="1" />
          <rect x="48" y="70" width="14" height="3" rx="1" />
          <rect x="85" y="70" width="14" height="3" rx="1" />
          <rect x="122" y="70" width="14" height="3" rx="1" />
          <rect x="48" y="83" width="14" height="3" rx="1" />
          <rect x="85" y="83" width="14" height="3" rx="1" />
        </g>
        <circle cx="110" cy="14" r="4.5" fill="#B91C1C" />
        <circle cx="110" cy="14" r="2.5" fill="#7F1D1D" />
        <ellipse cx="111" cy="12.5" rx="1.5" ry="0.7" fill="white" fillOpacity="0.55" />
      </g>
    </svg>
  );
}

export function CanadaHostedIllustration() {
  return (
    <svg
      viewBox="0 0 220 110"
      role="img"
      aria-label="The Canadian flag's red maple leaf next to a secure audit log document with verified entries"
      className="w-full h-24"
    >
      <image
        href="/canadian-flag-leaf.png"
        x="13"
        y="13"
        width="86"
        height="86"
        preserveAspectRatio="xMidYMid meet"
      />
      <g transform="translate(112 18)">
        <rect x="0" y="0" width="86" height="74" rx="3" fill="white" stroke="hsl(var(--brand-navy))" strokeWidth="1.5" />
        <rect x="0" y="0" width="86" height="14" fill="hsl(var(--brand-navy))" />
        <text x="6" y="9" fontSize="6" fill="white" fontWeight="600">AUDIT LOG</text>
        <g transform="translate(72 4)">
          <rect x="0" y="2" width="6" height="5" rx="0.5" fill="hsl(var(--brand-sky))" />
          <path d="M1 2 L1 0.5 a 2 2 0 0 1 4 0 L 5 2" fill="none" stroke="hsl(var(--brand-sky))" strokeWidth="1" />
        </g>
        <g transform="translate(0 18)">
          <rect x="6" y="0" width="50" height="3" rx="1" fill="hsl(var(--brand-sky))" />
          <circle cx="74" cy="1.5" r="3" fill="hsl(var(--brand-green))" />
          <path d="M72 1.5 L73.5 3 L76 0.5" fill="none" stroke="white" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
          <rect x="6" y="9" width="44" height="3" rx="1" fill="hsl(var(--brand-sky))" />
          <circle cx="74" cy="10.5" r="3" fill="hsl(var(--brand-green))" />
          <path d="M72 10.5 L73.5 12 L76 9.5" fill="none" stroke="white" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
          <rect x="6" y="18" width="56" height="3" rx="1" fill="hsl(var(--brand-sky))" />
          <circle cx="74" cy="19.5" r="3" fill="hsl(var(--brand-green))" />
          <path d="M72 19.5 L73.5 21 L76 18.5" fill="none" stroke="white" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
          <rect x="6" y="27" width="48" height="3" rx="1" fill="hsl(var(--brand-sky))" />
          <circle cx="74" cy="28.5" r="3" fill="hsl(var(--brand-green))" />
          <path d="M72 28.5 L73.5 30 L76 27.5" fill="none" stroke="white" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
          <rect x="6" y="36" width="40" height="3" rx="1" fill="hsl(var(--brand-sky))" />
          <circle cx="74" cy="37.5" r="3" fill="hsl(var(--brand-green))" />
          <path d="M72 37.5 L73.5 39 L76 36.5" fill="none" stroke="white" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
        </g>
      </g>
    </svg>
  );
}

export function FastSetupIllustration() {
  return (
    <svg viewBox="0 0 220 110" role="img" aria-label="A stopwatch showing the time to first published schedule, with a green checkmark" className="w-full h-24">
      <rect x="92" y="14" width="16" height="6" rx="1.5" fill="hsl(var(--brand-navy))" />
      <rect x="89" y="11" width="22" height="4" rx="1" fill="hsl(var(--brand-navy))" />
      <rect x="135" y="35" width="7" height="7" rx="1" fill="hsl(var(--brand-navy))" />
      <circle cx="100" cy="60" r="32" fill="white" stroke="hsl(var(--brand-navy))" strokeWidth="2.5" />
      <g stroke="hsl(var(--brand-navy))" strokeWidth="1.5">
        <line x1="100" y1="32" x2="100" y2="36" />
        <line x1="128" y1="60" x2="124" y2="60" />
        <line x1="100" y1="88" x2="100" y2="84" />
        <line x1="72" y1="60" x2="76" y2="60" />
      </g>
      <line x1="100" y1="60" x2="116" y2="44" stroke="hsl(var(--brand-green))" strokeWidth="2.5" strokeLinecap="round" />
      <circle cx="100" cy="60" r="3" fill="hsl(var(--brand-green))" />
      <circle cx="155" cy="78" r="13" fill="hsl(var(--brand-green))" />
      <path d="M 149 78 L 153 82 L 161 73" stroke="white" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
